import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  Check, X, ArrowRight, Scale, Shield, Fuel, Gauge, Users, Zap, Award,
  Sparkles, Wallet, Calculator, TrendingUp, ChevronDown, MapPin, ExternalLink, Store,
} from "lucide-react";
import { api, formatINR, formatINRFull } from "@/lib/api";
import { SEO } from "@/components/common/SEO";
import CarCard from "@/components/common/CarCard";
import SmartImage from "@/components/common/SmartImage";
import LeadForm from "@/components/common/LeadForm";
import { oemFor } from "@/lib/oem";

const BREAKDOWN_ICON = {
  Performance: Zap, Safety: Shield, Features: Sparkles, Comfort: Users,
  Mileage: Gauge, Maintenance: Wallet, "Value for Money": TrendingUp, Resale: Award,
};

function ScoreRing({ value }) {
  const r = 42, c = 2 * Math.PI * r, off = c - (Math.max(0, Math.min(100, value)) / 100) * c;
  const color = value >= 75 ? "#10b981" : value >= 55 ? "#3b82f6" : value >= 40 ? "#f59e0b" : "#ef4444";
  return (
    <svg viewBox="0 0 100 100" className="w-24 h-24" data-testid="trending-score-ring">
      <circle cx="50" cy="50" r={r} fill="none" stroke="currentColor" strokeOpacity="0.12" strokeWidth="8"/>
      <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth="8"
        strokeDasharray={c} strokeDashoffset={off} strokeLinecap="round"
        transform="rotate(-90 50 50)" style={{ transition: "stroke-dashoffset 900ms ease" }}/>
      <text x="50" y="55" textAnchor="middle" className="fill-foreground" fontSize="22" fontWeight="700">{value}</text>
    </svg>
  );
}

function Bar({ label, value }) {
  const Icon = BREAKDOWN_ICON[label] || TrendingUp;
  return (
    <div data-testid={`score-bar-${label.toLowerCase().replace(/\s+/g, "-")}`}>
      <div className="flex items-center justify-between text-sm mb-1.5">
        <span className="inline-flex items-center gap-2 text-muted-foreground"><Icon className="w-3.5 h-3.5"/>{label}</span>
        <span className="font-mono font-medium">{value}</span>
      </div>
      <div className="h-2 rounded-full bg-secondary overflow-hidden">
        <div className="h-full rounded-full bg-gradient-to-r from-blue-500 to-emerald-500 transition-all duration-700" style={{ width: `${value}%` }}/>
      </div>
    </div>
  );
}

function FAQ({ q, a, idx }) {
  const [open, setOpen] = useState(idx === 0);
  return (
    <div className="border-b border-border py-4" data-testid={`faq-${idx}`}>
      <button onClick={() => setOpen(v => !v)} className="w-full flex items-center justify-between text-left gap-4">
        <span className="font-medium">{q}</span>
        <ChevronDown className={`w-4 h-4 shrink-0 transition-transform ${open ? "rotate-180" : ""}`}/>
      </button>
      {open && <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{a}</p>}
    </div>
  );
}

export default function CarDetail() {
  const { slug } = useParams();
  const [car, setCar] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [score, setScore] = useState(null);
  const [competitors, setCompetitors] = useState([]);
  const [articles, setArticles] = useState([]);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    setCar(null); setScore(null); setActiveImg(0); setNotFound(false);
    api.get(`/cars/${slug}`).then(r => setCar(r.data)).catch(() => setNotFound(true));
    api.get(`/cars/${slug}/trending-score`).then(r => setScore(r.data)).catch(() => {});
    api.get(`/cars/${slug}/competitors?limit=6`).then(r => setCompetitors(r.data)).catch(() => {});
    api.get(`/articles?limit=3`).then(r => setArticles(r.data)).catch(() => {});
  }, [slug]);

  if (notFound) return (
    <div className="max-w-3xl mx-auto px-6 lg:px-10 py-24 text-center" data-testid="car-not-found">
      <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">404</div>
      <h1 className="font-display text-4xl sm:text-5xl mt-3 tracking-tight">Car not found</h1>
      <p className="text-muted-foreground mt-4">We couldn't find that car. It may have been moved or is no longer listed.</p>
      <Link to="/cars" data-testid="back-to-cars" className="btn-primary h-11 px-6 text-sm font-medium inline-flex items-center gap-2 mt-8">Browse all cars <ArrowRight className="w-4 h-4"/></Link>
    </div>
  );

  if (!car) return <div className="max-w-7xl mx-auto px-6 lg:px-10 py-20 text-muted-foreground" data-testid="car-detail-loading">Loading…</div>;

  const sp = car.specs || {};
  const priceMin = car.price_min;
  const hasPrice = typeof priceMin === "number" && priceMin > 0;
  const isMinimal = !hasPrice && Object.keys(sp).length === 0;
  // Simple deterministic estimates (no hooks after early return)
  const _p = (priceMin || 0) * 0.8, _r = 0.095 / 12, _n = 60;
  const emi = hasPrice ? Math.round((_p * _r * Math.pow(1 + _r, _n)) / (Math.pow(1 + _r, _n) - 1)) : 0;
  const insurance = hasPrice ? Math.round(priceMin * 0.03) : 0;
  const gallery = (car.images && car.images.length ? car.images : [car.image]);
  const isEV = car.fuel_types?.includes("EV");

  // Ownership: cost per km rough estimate
  const perKm = isEV ? 1.8 : (105 / (sp.mileage_kmpl || 15));
  const monthlyRunning = Math.round(perKm * 40 * 30);

  const highlights = [
    { icon: Shield, label: "Safety", value: sp.ncap_rating ? `${sp.ncap_rating}★ NCAP` : `${sp.airbags || "—"} airbags` },
    { icon: isEV ? Zap : Fuel, label: isEV ? "Range" : "Mileage", value: isEV ? `${sp.range_km || "—"} km` : `${sp.mileage_kmpl || "—"} kmpl` },
    { icon: Users, label: "Seats", value: sp.seats || "—" },
    { icon: Gauge, label: "Power", value: sp.power_bhp ? `${sp.power_bhp} bhp` : "—" },
  ];

  const specGroups = [
    { title: "Engine & Performance", rows: [
      ["Engine", sp.engine], ["Power", sp.power_bhp ? `${sp.power_bhp} bhp` : null],
      ["Torque", sp.torque_nm ? `${sp.torque_nm} Nm` : null], ["Drive", sp.drive],
    ]},
    { title: "Fuel & Efficiency", rows: [
      ["Fuel Types", (car.fuel_types || []).join(", ")],
      ["Mileage", sp.mileage_kmpl ? `${sp.mileage_kmpl} kmpl` : null],
      ["Range (EV)", sp.range_km ? `${sp.range_km} km` : null],
      ["Battery", sp.battery_kwh ? `${sp.battery_kwh} kWh` : null],
      ["Fuel Tank", sp.fuel_tank_litres ? `${sp.fuel_tank_litres} L` : null],
    ]},
    { title: "Comfort & Space", rows: [
      ["Seats", sp.seats], ["Boot", sp.boot_litres ? `${sp.boot_litres} L` : null],
      ["Sunroof", sp.sunroof ? "Yes" : null], ["Auto Climate", sp.auto_climate ? "Yes" : null],
    ]},
    { title: "Safety & Technology", rows: [
      ["Airbags", sp.airbags], ["NCAP Rating", sp.ncap_rating ? `${sp.ncap_rating}★` : null],
      ["ADAS", sp.adas ? "Yes" : null], ["Touchscreen", sp.touchscreen_inches ? `${sp.touchscreen_inches}"` : null],
    ]},
  ].map(g => ({ ...g, rows: g.rows.filter(([, v]) => v !== null && v !== undefined && v !== false && v !== "") }));

  const shouldBuy = [
    sp.ncap_rating >= 4 && "You prioritise crash safety and family peace-of-mind",
    isEV && "You have home/workplace charging and drive under 120 km/day",
    (sp.mileage_kmpl || 0) >= 20 && "Fuel efficiency is your top concern",
    sp.adas && "You want the latest driver-assistance technology",
    (sp.seats || 5) >= 7 && "You need genuine 6- or 7-seater flexibility",
    car.price_min < 1000000 && "You're looking for maximum value under ₹10L",
  ].filter(Boolean).slice(0, 4);

  const shouldAvoid = [
    isEV && "You do long inter-city trips weekly without planning charging stops",
    !sp.adas && car.price_min > 1500000 && "You want ADAS as a must-have at this budget",
    (sp.mileage_kmpl || 999) < 14 && !isEV && "You drive 60+ km/day and running cost matters most",
    (sp.boot_litres || 500) < 300 && "Weekly luggage-heavy trips are common in your family",
  ].filter(Boolean).slice(0, 3);

  const faqs = [
    { q: `What is the on-road price of ${car.brand_name} ${car.model}?`,
      a: `Ex-showroom starts at ${formatINR(car.price_min)}. On-road (with 10% RTO + insurance + fastag) is roughly ${formatINR(Math.round(car.price_min * 1.14))} in most metros. Prices vary by state and variant.` },
    { q: `What is the mileage/${isEV ? "range" : "fuel economy"} of the ${car.model}?`,
      a: isEV ? `The ${car.model} offers a claimed range of ${sp.range_km || "—"} km on a full charge with a ${sp.battery_kwh || "—"} kWh battery. Real-world range is typically 80–85% of claimed.`
              : `ARAI mileage is around ${sp.mileage_kmpl || "—"} kmpl. Real-world you can expect 75–85% of that number depending on city vs highway driving.` },
    { q: `Is the ${car.model} safe?`,
      a: `It comes with ${sp.airbags || "the standard"} airbags${sp.ncap_rating ? ` and a ${sp.ncap_rating}-star crash rating` : ""}. ${sp.adas ? "ADAS driver-assist features add another layer of protection." : ""}` },
    { q: `Which variant of the ${car.model} should I buy?`,
      a: `The mid-variant typically offers the best value-for-money. Top variants only make sense if you specifically want sunroof, ADAS or the largest touchscreen. Skip the base variant unless budget is tight.` },
    { q: `What are the main rivals of the ${car.model}?`,
      a: competitors.length ? competitors.slice(0, 3).map(c => `${c.brand_name} ${c.model}`).join(", ") + ` are the closest competitors.`
                            : "Direct rivals in this segment vary by budget and body type." },
  ];

  return (
    <div data-testid="car-detail-page">
      <SEO
        title={car.meta_title || `${car.brand_name} ${car.model} — Price, Specs, Mileage, Review`}
        description={car.meta_description || `${car.brand_name} ${car.model} in India: price starts at ${formatINR(car.price_min)}. Full specs, variants, pros, cons, EMI, insurance & competitor comparison.`}
        image={car.og_image || car.image}/>
      <script type="application/ld+json">
        {JSON.stringify({
          "@context": "https://schema.org", "@type": "Product",
          name: `${car.brand_name} ${car.model}`, image: gallery,
          brand: { "@type": "Brand", name: car.brand_name },
          offers: { "@type": "AggregateOffer", lowPrice: car.price_min, highPrice: car.price_max, priceCurrency: "INR" },
        })}
      </script>

      {/* HERO */}
      <section className="relative border-b border-border">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid lg:grid-cols-12 gap-8">
          <div className="lg:col-span-7">
            <div className="aspect-[16/10] rounded-3xl overflow-hidden border border-border bg-secondary" data-testid="hero-gallery">
              <SmartImage src={gallery[activeImg]} alt={`${car.brand_name} ${car.model}`} eager label={`${car.brand_name} ${car.model}`}/>
            </div>
            {gallery.length > 1 && (
              <div className="mt-3 flex gap-2 overflow-x-auto">
                {gallery.map((g, i) => (
                  <button key={g} data-testid={`gallery-thumb-${i}`} onClick={() => setActiveImg(i)}
                    className={`w-20 h-14 rounded-xl overflow-hidden border-2 shrink-0 ${activeImg === i ? "border-blue-500" : "border-transparent opacity-70 hover:opacity-100"}`}>
                    <SmartImage src={g} alt="" compact/>
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="lg:col-span-5">
            <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground flex items-center gap-2">
              <Link to={`/cars?brand=${car.brand_slug}`} className="hover:underline">{car.brand_name}</Link>
              <span>·</span><span>{car.body_type}</span>
              {score && !isMinimal && <span className="ml-2 px-2 py-0.5 rounded-full bg-emerald-50 text-emerald-700 dark:bg-emerald-950 dark:text-emerald-300 text-[10px]">Trending {score.overall}</span>}
            </div>
            <h1 className="font-display text-4xl sm:text-5xl mt-2 tracking-tighter">{car.brand_name} {car.model}</h1>
            {car.tagline && <p className="text-muted-foreground mt-3">{car.tagline}</p>}
            <div className="mt-6">
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Ex-showroom price</div>
              {hasPrice ? (
                <div className="mt-1 inline-flex items-baseline gap-3">
                  <div className="font-display text-3xl">{formatINR(car.price_min)}</div>
                  {car.price_max > car.price_min && (<><span className="text-muted-foreground">—</span><div className="font-display text-3xl">{formatINR(car.price_max)}</div></>)}
                </div>
              ) : (
                <div className="font-display text-2xl mt-1 text-muted-foreground" data-testid="price-coming-soon">Price coming soon</div>
              )}
            </div>

            {isMinimal && (
              <div className="mt-6 rounded-2xl border border-blue-200 dark:border-blue-900 bg-blue-50/60 dark:bg-blue-950/30 p-4 text-sm text-blue-800 dark:text-blue-200" data-testid="details-coming-soon">
                Full specifications, variants and pricing for the {car.brand_name} {car.model} are being finalised and will be published soon.
              </div>
            )}

            {/* Quick highlights */}
            {!isMinimal && (
            <div className="mt-6 grid grid-cols-2 gap-3">
              {highlights.map(h => (
                <div key={h.label} className="rounded-2xl border border-border p-3">
                  <div className="text-[11px] uppercase tracking-[0.15em] text-muted-foreground inline-flex items-center gap-1.5"><h.icon className="w-3 h-3"/>{h.label}</div>
                  <div className="font-medium mt-1">{h.value}</div>
                </div>
              ))}
            </div>
            )}

            <div className="mt-6 flex flex-wrap gap-3">
              <Link to={`/compare?slugs=${car.slug}`} data-testid="cta-compare" className="btn-primary h-11 px-5 text-sm font-medium inline-flex items-center gap-2">
                <Scale className="w-4 h-4"/> Compare
              </Link>
              {hasPrice && (
              <a href="#emi" data-testid="cta-emi" className="btn-secondary h-11 px-5 text-sm font-medium inline-flex items-center gap-2">
                <Calculator className="w-4 h-4"/> Check EMI
              </a>
              )}
              <Link to={`/chat?q=${encodeURIComponent(`Is the ${car.brand_name} ${car.model} right for me?`)}`}
                data-testid="cta-ask-expert" className="btn-secondary h-11 px-5 text-sm font-medium inline-flex items-center gap-2">
                <Sparkles className="w-4 h-4 text-ai"/> Ask Expert
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* TRENDING SCORE */}
      {score && !isMinimal && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-12" data-testid="section-trending-score">
          <div className="rounded-3xl border border-border p-6 lg:p-8 grid lg:grid-cols-12 gap-8 items-center bg-card">
            <div className="lg:col-span-3 flex flex-col items-center text-center">
              <ScoreRing value={score.overall}/>
              <div className="mt-3 text-xs uppercase tracking-[0.2em] text-muted-foreground">Trending Score</div>
              <div className="text-sm mt-1 text-muted-foreground">out of 100</div>
            </div>
            <div className="lg:col-span-9 grid sm:grid-cols-2 gap-x-8 gap-y-4">
              {Object.entries(score.breakdown).map(([k, v]) => <Bar key={k} label={k} value={v}/>)}
            </div>
          </div>
        </section>
      )}

      {/* SPECIFICATIONS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="section-specs">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Detailed</div>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Specifications & Features</h2>
        <div className="mt-6 grid md:grid-cols-2 gap-5">
          {specGroups.filter(g => g.rows.length).map(g => (
            <div key={g.title} className="rounded-3xl border border-border overflow-hidden bg-card" data-testid={`spec-group-${g.title.split(" ")[0].toLowerCase()}`}>
              <div className="px-5 py-3 border-b border-border bg-secondary/40 font-medium">{g.title}</div>
              <div className="divide-y divide-border">
                {g.rows.map(([k, v]) => (
                  <div key={k} className="flex justify-between py-3 px-5 text-sm">
                    <span className="text-muted-foreground">{k}</span>
                    <span className="font-medium text-right">{String(v)}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* VARIANTS */}
      {car.variants && car.variants.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="section-variants">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">All variants</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Pick your trim</h2>
          <div className="mt-6 rounded-3xl border border-border overflow-hidden bg-card">
            <table className="w-full text-sm">
              <thead className="bg-secondary/40">
                <tr>
                  <th className="text-left p-4 font-medium">Variant</th>
                  <th className="text-left p-4 font-medium hidden sm:table-cell">Fuel</th>
                  <th className="text-left p-4 font-medium hidden sm:table-cell">Transmission</th>
                  <th className="text-right p-4 font-medium">Ex-showroom</th>
                </tr>
              </thead>
              <tbody>
                {car.variants.map(v => (
                  <tr key={v.name} className="border-t border-border">
                    <td className="p-4 font-medium">{v.name}</td>
                    <td className="p-4 hidden sm:table-cell text-muted-foreground">{v.fuel}</td>
                    <td className="p-4 hidden sm:table-cell text-muted-foreground">{v.transmission}</td>
                    <td className="p-4 text-right font-mono">{formatINR(v.price)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      )}

      {/* OWNERSHIP + EMI + INSURANCE */}
      {hasPrice && (
      <section id="emi" className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="section-ownership">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Cost of ownership</div>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">What will it really cost you?</h2>
        <div className="mt-6 grid md:grid-cols-3 gap-5">
          <div className="rounded-3xl border border-border p-6 bg-card" data-testid="card-emi">
            <div className="text-[11px] uppercase tracking-[0.2em] text-blue-600 font-semibold">EMI (indicative)</div>
            <div className="font-display text-3xl mt-2">{formatINRFull(emi)}<span className="text-sm text-muted-foreground font-normal">/mo</span></div>
            <div className="text-xs text-muted-foreground mt-2">80% financed · 9.5% p.a. · 60 months</div>
            <Link to={`/tools/emi?price=${car.price_min}`} className="mt-4 inline-flex text-xs text-blue-600 hover:underline">Customize EMI →</Link>
          </div>
          <div className="rounded-3xl border border-border p-6 bg-card" data-testid="card-insurance">
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-600 font-semibold">1st year insurance</div>
            <div className="font-display text-3xl mt-2">{formatINRFull(insurance)}</div>
            <div className="text-xs text-muted-foreground mt-2">Comprehensive · zero-dep add-on recommended</div>
            <Link to={`/tools/insurance?price=${car.price_min}`} className="mt-4 inline-flex text-xs text-emerald-600 hover:underline">Estimate premium →</Link>
          </div>
          <div className="rounded-3xl border border-border p-6 bg-card" data-testid="card-running">
            <div className="text-[11px] uppercase tracking-[0.2em] text-amber-600 font-semibold">Monthly running cost</div>
            <div className="font-display text-3xl mt-2">{formatINRFull(monthlyRunning)}</div>
            <div className="text-xs text-muted-foreground mt-2">40 km/day · {isEV ? "electricity @ ₹8/kWh" : "petrol @ ₹105/L"}</div>
            <Link to={`/tools/ownership?price=${car.price_min}&fuel=${car.fuel_types[0]}`} className="mt-4 inline-flex text-xs text-amber-600 hover:underline">Full ownership cost →</Link>
          </div>
        </div>
      </section>
      )}

      {/* PROS & CONS */}
      {(car.ai_pros?.length || car.ai_cons?.length) ? (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="section-pros-cons">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Honest take</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Pros & Cons</h2>
          <div className="mt-6 grid md:grid-cols-2 gap-5">
            <div className="rounded-3xl border border-border p-6 bg-emerald-50/40 dark:bg-emerald-950/20">
              <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 font-semibold mb-4 inline-flex items-center gap-1"><Check className="w-3.5 h-3.5"/> What we love</div>
              <ul className="space-y-3">
                {(car.ai_pros || []).map((p) => (
                  <li key={p} className="flex gap-2 text-sm"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>{p}</li>
                ))}
              </ul>
            </div>
            <div className="rounded-3xl border border-border p-6 bg-rose-50/40 dark:bg-rose-950/20">
              <div className="text-[11px] uppercase tracking-[0.2em] text-rose-700 dark:text-rose-400 font-semibold mb-4 inline-flex items-center gap-1"><X className="w-3.5 h-3.5"/> Where it slips</div>
              <ul className="space-y-3">
                {(car.ai_cons || []).map((p) => (
                  <li key={p} className="flex gap-2 text-sm"><X className="w-4 h-4 text-rose-600 mt-0.5 shrink-0"/>{p}</li>
                ))}
              </ul>
            </div>
          </div>
        </section>
      ) : null}

      {/* WHO SHOULD BUY / AVOID */}
      {!isMinimal && (
      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="section-buy-guide">
        <div className="grid md:grid-cols-2 gap-5">
          <div className="rounded-3xl border-2 border-emerald-200 dark:border-emerald-900 p-6 bg-card">
            <div className="text-[11px] uppercase tracking-[0.2em] text-emerald-700 dark:text-emerald-400 font-semibold">Who should buy</div>
            <h3 className="font-display text-2xl mt-2 tracking-tight">Pick the {car.model} if…</h3>
            <ul className="mt-4 space-y-3">
              {shouldBuy.length ? shouldBuy.map((r) => (
                <li key={r} className="flex gap-2 text-sm"><Check className="w-4 h-4 text-emerald-600 mt-0.5 shrink-0"/>{r}</li>
              )) : <li className="text-sm text-muted-foreground">You want a well-rounded pick in the {car.body_type} segment.</li>}
            </ul>
          </div>
          <div className="rounded-3xl border-2 border-rose-200 dark:border-rose-900 p-6 bg-card">
            <div className="text-[11px] uppercase tracking-[0.2em] text-rose-700 dark:text-rose-400 font-semibold">Who should skip</div>
            <h3 className="font-display text-2xl mt-2 tracking-tight">Avoid if…</h3>
            <ul className="mt-4 space-y-3">
              {shouldAvoid.length ? shouldAvoid.map((r) => (
                <li key={r} className="flex gap-2 text-sm"><X className="w-4 h-4 text-rose-600 mt-0.5 shrink-0"/>{r}</li>
              )) : <li className="text-sm text-muted-foreground">Your primary need falls outside this body type or price bracket.</li>}
            </ul>
          </div>
        </div>
      </section>
      )}

      {/* COMPETITORS */}
      {competitors.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="section-competitors">
          <div className="flex items-end justify-between flex-wrap gap-4 mb-6">
            <div>
              <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Head to head</div>
              <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Competitors to consider</h2>
            </div>
            <Link to={`/compare?slugs=${car.slug},${competitors[0].slug}`} className="text-sm text-blue-600 hover:underline">Compare all →</Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {competitors.slice(0, 4).map(c => <CarCard key={c.slug} car={c}/>)}
          </div>
        </section>
      )}

      {/* RELATED ARTICLES */}
      {articles.length > 0 && (
        <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10" data-testid="section-related-articles">
          <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">Read next</div>
          <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Related reviews & guides</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-5">
            {articles.slice(0, 3).map(a => (
              <Link key={a.slug} to={`/news/${a.slug}`} className="rounded-3xl overflow-hidden border border-border bg-card hover:shadow-lg transition-all group">
                <div className="aspect-[16/9] overflow-hidden"><SmartImage src={a.hero_image} alt={a.title} label={a.title} className="group-hover:scale-[1.03] transition-transform"/></div>
                <div className="p-5">
                  <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">{a.category} · {a.read_minutes} min</div>
                  <div className="font-display text-lg mt-2 tracking-tight leading-snug line-clamp-2">{a.title}</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* FAQ */}
      {!isMinimal && (
      <section className="max-w-4xl mx-auto px-6 lg:px-10 py-14" data-testid="section-faq">
        <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">FAQs</div>
        <h2 className="font-display text-3xl sm:text-4xl mt-2 tracking-tight">Common questions</h2>
        <div className="mt-6">
          {faqs.map((f, i) => <FAQ key={f.q} idx={i} q={f.q} a={f.a}/>)}
        </div>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org", "@type": "FAQPage",
            mainEntity: faqs.map(f => ({ "@type": "Question", name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a } })),
          })}
        </script>
      </section>
      )}

      {/* Bottom: Lead-gen widget + OEM deep-links */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-16" data-testid="section-lead">
        <div className="grid lg:grid-cols-5 gap-6">
          <div className="lg:col-span-3">
            <LeadForm car={car}/>
          </div>
          <div className="lg:col-span-2 space-y-4">
            {(() => {
              const oem = oemFor(car.brand_slug);
              if (!oem) {
                return (
                  <div className="rounded-3xl border border-border bg-card p-6" data-testid="oem-links-missing">
                    <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Also on TrendingNewCar</div>
                    <h4 className="font-display text-xl mt-2">Keep exploring</h4>
                    <div className="mt-4 space-y-2">
                      <Link to={`/compare?slugs=${car.slug}`} className="w-full h-11 rounded-full border border-border bg-white dark:bg-card text-sm font-medium inline-flex items-center justify-between px-5 hover:bg-secondary">
                        <span className="inline-flex items-center gap-2"><Scale className="w-4 h-4"/> Compare with rivals</span>
                        <ArrowRight className="w-4 h-4"/>
                      </Link>
                      <Link to={`/chat?q=${encodeURIComponent(`Should I buy the ${car.brand_name} ${car.model}?`)}`}
                        className="w-full h-11 rounded-full border border-border bg-white dark:bg-card text-sm font-medium inline-flex items-center justify-between px-5 hover:bg-secondary">
                        <span className="inline-flex items-center gap-2"><Sparkles className="w-4 h-4 text-ai"/> Ask our AI expert</span>
                        <ArrowRight className="w-4 h-4"/>
                      </Link>
                    </div>
                  </div>
                );
              }
              return (
                <div className="rounded-3xl border border-border bg-card p-6" data-testid="oem-links">
                  <div className="text-[11px] uppercase tracking-[0.2em] text-muted-foreground">Prefer to go direct?</div>
                  <h4 className="font-display text-xl mt-2 tracking-tight">Visit {oem.label}</h4>
                  <p className="text-sm text-muted-foreground mt-2">Official brochures, on-road configurator and authorised showroom locator.</p>
                  <div className="mt-4 space-y-2">
                    <a href={oem.site} target="_blank" rel="noopener noreferrer" data-testid="oem-site-link"
                      className="w-full h-11 rounded-full border border-border bg-white dark:bg-card text-sm font-medium inline-flex items-center justify-between px-5 hover:bg-secondary hover:border-blue-400 transition-colors">
                      <span className="inline-flex items-center gap-2"><ExternalLink className="w-4 h-4"/> Official website</span>
                      <ArrowRight className="w-4 h-4"/>
                    </a>
                    <a href={oem.dealer} target="_blank" rel="noopener noreferrer" data-testid="oem-dealer-link"
                      className="w-full h-11 rounded-full border border-border bg-white dark:bg-card text-sm font-medium inline-flex items-center justify-between px-5 hover:bg-secondary hover:border-emerald-400 transition-colors">
                      <span className="inline-flex items-center gap-2"><Store className="w-4 h-4"/> Find nearest showroom</span>
                      <ArrowRight className="w-4 h-4"/>
                    </a>
                    <Link to={`/compare?slugs=${car.slug}`} data-testid="cta-compare-secondary"
                      className="w-full h-11 rounded-full border border-border bg-white dark:bg-card text-sm font-medium inline-flex items-center justify-between px-5 hover:bg-secondary">
                      <span className="inline-flex items-center gap-2"><Scale className="w-4 h-4"/> Compare with rivals</span>
                      <ArrowRight className="w-4 h-4"/>
                    </Link>
                  </div>
                </div>
              );
            })()}
            <Link to={`/chat?q=${encodeURIComponent(`Should I buy the ${car.brand_name} ${car.model}?`)}`}
              data-testid="cta-ask-expert-secondary"
              className="block rounded-3xl border border-blue-200 dark:border-blue-900 bg-gradient-to-br from-blue-50 to-emerald-50/60 dark:from-blue-950/40 dark:to-emerald-950/30 p-6 hover:shadow-md transition-shadow">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-2xl bg-gradient-to-br from-blue-600 to-emerald-500 text-white grid place-items-center shrink-0">
                  <Sparkles className="w-4 h-4"/>
                </div>
                <div>
                  <div className="font-display text-lg tracking-tight">Not sure yet? Ask our AI expert</div>
                  <div className="text-xs text-muted-foreground mt-0.5">Personalised advice in seconds — free.</div>
                </div>
              </div>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
